import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Typography,
    Button,
    CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from './SnackbarContext';
import Successmark from '../assets/Successmark.svg';

const useStyles = makeStyles(() => ({
    rootContainer: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
        position: 'relative',
    },
    successCard: {
        width: '100%',
        maxWidth: 360,
        margin: '16px auto',
        background: 'white',
        overflow: 'visible',
        borderRadius: 30,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        paddingBottom: 20,
    },
    successIcon: {
        width: 80,
        height: 80,
        left: '50%',
        top: 120,
        position: 'absolute',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    title: {
        width: 275,
        left: 37,
        top: 220,
        position: 'absolute',
        color: '#C084FC',
        fontSize: 30.26,
        fontFamily: 'Poppins',
        fontWeight: '700',
        lineHeight: '30.75px',
        wordWrap: 'break-word',
        textAlign: 'center',
    },
    subtitle: {
        width: 285,
        left: 37,
        top: 280,
        position: 'absolute',
        color: '#A1A1A1',
        fontSize: 16,
        fontFamily: 'Nunito',
        fontWeight: '500',
        lineHeight: '24px',
        wordWrap: 'break-word',
        textAlign: 'center',
    },
    message: {
        width: 285,
        left: 37,
        top: 340,
        position: 'absolute',
        color: '#666666',
        fontSize: 14,
        fontFamily: 'Nunito',
        fontWeight: '400',
        lineHeight: '20px',
        wordWrap: 'break-word',
        textAlign: 'center',
    },
    signInButton: {
        width: 285,
        height: 51,
        left: 37,
        top: 480,
        position: 'absolute',
        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
        boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
        borderRadius: 10.40,
        border: 'none',
        color: 'white',
        fontSize: 18,
        fontFamily: 'Poppins',
        fontWeight: '700',
        cursor: 'pointer',
        '&:hover': {
            background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
        },
        '&:disabled': {
            background: '#E0E0E0',
            color: '#9E9E9E',
        },
    },
    loadingSpinner: {
        color: 'white',
    },
}));

const SignupSuccess = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);

    const { managerData, hotelId } = location.state || {};

    useEffect(() => {
        if (!managerData || !hotelId) {
            navigate('/associates-login');
        }
    }, []);

    const handleSignIn = () => {
        setIsLoading(true);
        // Navigate to associates login
        setTimeout(() => {
            navigate('/associates-login');
        }, 500);
    };

    return (
        <div className={classes.rootContainer}>
            <div className={classes.successCard}>
                {/* Scrollable Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
                    {/* Success Icon */}
                    <div style={{
                        width: 80,
                        height: 80,
                        margin: '40px auto 20px auto',
                        background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }}>
                        <img src={Successmark} alt="Success" style={{ width: '60%', height: '60%' }} />
                    </div>

                    {/* Title */}
                    <div style={{
                        color: '#C084FC',
                        fontSize: 30.26,
                        fontFamily: 'Poppins',
                        fontWeight: '700',
                        lineHeight: '30.75px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        marginBottom: 20
                    }}>
                        Registration Successful!
                    </div>

                    {/* Subtitle */}
                    <div style={{
                        color: '#A1A1A1',
                        fontSize: 16,
                        fontFamily: 'Nunito',
                        fontWeight: '500',
                        lineHeight: '24px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        marginBottom: 20
                    }}>
                        Your property has been registered successfully.
                    </div>

                    {/* Message */}
                    <div style={{
                        color: '#666666',
                        fontSize: 14,
                        fontFamily: 'Nunito',
                        fontWeight: '400',
                        lineHeight: '20px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        marginBottom: 30
                    }}>
                        Manager account has been created and verified. You can now sign in to access your dashboard.
                    </div>

                    {/* Login Credentials */}
                    {managerData?.loginCredentials && (
                        <div style={{
                            width: '100%',
                            background: '#F8F9FA',
                            borderRadius: 10,
                            padding: 15,
                            border: '1px solid #E9ECEF',
                            marginBottom: 30
                        }}>
                            <div style={{
                                color: '#495057',
                                fontSize: 12,
                                fontFamily: 'Nunito',
                                fontWeight: '600',
                                marginBottom: 8
                            }}>
                                Your Login Credentials:
                            </div>
                            <div style={{
                                color: '#6C757D',
                                fontSize: 11,
                                fontFamily: 'Nunito',
                                lineHeight: '16px',
                                marginBottom: 4
                            }}>
                                Email: {managerData.loginCredentials.email}
                            </div>
                            <div style={{
                                color: '#6C757D',
                                fontSize: 11,
                                fontFamily: 'Nunito',
                                lineHeight: '16px'
                            }}>
                                Password: {managerData.loginCredentials.password}
                            </div>
                        </div>
                    )}
                </div>

                {/* Fixed Sign In Button at Bottom */}
                <div style={{ padding: '20px', borderTop: '1px solid #f0f0f0' }}>
                    <button
                        style={{
                            width: '100%',
                            height: 51,
                            background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
                            boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
                            borderRadius: 10.40,
                            border: 'none',
                            color: 'white',
                            fontSize: 18,
                            fontFamily: 'Poppins',
                            fontWeight: '700',
                            cursor: 'pointer',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
                            },
                            '&:disabled': {
                                background: '#E0E0E0',
                                color: '#9E9E9E',
                            },
                        }}
                        onClick={handleSignIn}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} className={classes.loadingSpinner} />
                        ) : (
                            'Sign In to Dashboard'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignupSuccess;
